//================================================================
//  Tab: IPAM (Subnets)
//================================================================

//  Purpose: This tab displays all 'IPAM (Subnets)'

//  Example:
//    <Subnets></Subnets>    

//================================================================

// Libraries
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Context
import { SetSortFilters, GetUser, GetRoles } from '../../../Library/GlobalContexts';

// Components
import PageComponent from '../../../Components/PageComponent/PageComponent';
import ExportToCSV from '../../../Components/ExportToCSV/ExportToCSV';
import AddSubnet from './SidePanes/AddSubnet';
import AddRegionSupernet from './SidePanes/AddRegionSupernet';
import TableHeader from '../../../Components/SortFilter/TableHeader';
import FilterTags from '../../../Components/SortFilter/FilterTags';
import FilterByPane from '../../../Components/SortFilter/FilterByPane';
import SubnetsAllocationGuide from './Components/SubnetsAllocationGuide/SubnetsAllocationGuide';

// Functions
import QueryListener from '../../../Library/QueryListener';
import ConvertDate from '../../../Library/ConvertDate';
import CheckInventoryRole from '../../../Library/CheckInventoryRole';

// Images
import IconSearch from '../../../Components/Images/Icon_Search_Grey.svg';
import IconAdd from '../../../Components/Images/Icon_Add_White.svg';


export default function Subnets() {

    //------------------------------------------------------
    //  useContext & React Router
    //------------------------------------------------------

    const navigate = useNavigate();
    const setSortFilters = useContext(SetSortFilters);
    
    const getUser = useContext(GetUser);
    const getRoles = useContext(GetRoles);

    //------------------------------------------------------
    //  useStates
    //------------------------------------------------------

    // Used to save page status > 'pending', 'onload', 'error-invalid', 'error-fatal'
    const [pageStatus, setPageStatus] = useState('pending');
    const [roleStatus, setRoleStatus] = useState('download'); // download, write & admin

    // Search input text for subnets table
    const [searchInput, setSearchInput] = useState('');

    // Used to store list of all subnets
    const [subnets, setSubnets] = useState([]);

    // Used to store filtered list of subnets
    const [subnetsFiltered, setSubnetsFiltered] = useState([]);

    //------------------------------------------------------
    //  Functions
    //------------------------------------------------------

    // Handles search function
    function handleSearch(value) {

        // Clear ALL filters & sorting
        if (value.length > 0 && value.length < 2) {

            setSortFilters({

                // Sorting
                'sortBy': '', // subnetip
                'sortDirection': '', // ascending & descending
                'sortDataType': '', // '' or 'number'

                // Filtering (Map of objects)
                'filters': {},

                // Sidepane for selecting filterby content
                'filterByPane': {
                    'columnname': '', // Subnet
                    'columnid': '', // subnetip
                }

            });

        }

        setSearchInput(value);

        // Reset the Filter
        if (value.length === 0) {

            setSubnetsFiltered(subnets);

        };

        // Search Filter
        const searchResults = subnets.filter((object) =>
            object?.subnetip?.toLowerCase().includes(value.toLowerCase()) ||
            object?.region?.toLowerCase().includes(value.toLowerCase()) ||
            object?.siteid?.toLowerCase().includes(value.toLowerCase()) ||
            object?.vlan?.toLowerCase().includes(value.toLowerCase()) ||
            object?.description?.toLowerCase().includes(value.toLowerCase()) ||
            object?.additionalinfo?.toLowerCase().includes(value.toLowerCase())
        );

        // Save the filtered search results to the useState
        setSubnetsFiltered(searchResults);

    };

    // Create data format for CSV Export
    function getDataObject(subnets) {

        const data = [];

        subnets.forEach((subnet) => {

            data.push({
                'Subnet': subnet.subnetip,
                'Region': subnet.region,
                'Supernet': subnet.supernet,
                'Site ID': subnet.siteid,
                'Vlan ID': subnet.vlan,
                'Description': subnet.description,
                'Netmask': subnet.subnetmask,
                'Hosts/Net': subnet.assignablehosts,
                'HostMin': subnet.hostmin,
                'HostMax': subnet.hostmax,
                'Additional Info (VRF, VPC, Zone ...)': subnet.additionalinfo,
                'Created': ConvertDate(subnet?.createddate),
                'Modified': ConvertDate(subnet?.lastmodifieddate),
            });

        });

        return data;
    }

    //------------------------------------------------------
    //  useEffects
    //------------------------------------------------------

    // Query Listener
    // - Get all subnets
    useEffect(() => {

        if (getRoles === undefined) return;
        if (getUser === undefined) return;

        // ============================================
        //  Perform role check
        // ============================================

        const roleStatus = CheckInventoryRole('subnets', getRoles, getUser);
        if (roleStatus === 'access-denied') return setPageStatus('access-denied');
        setRoleStatus(roleStatus);

        // ============================================
        //  Fetch Data
        // ============================================

        function onLoad(documents) {

            setSubnets(documents);
            setSubnetsFiltered(documents);

            setPageStatus('onload');

        }

        function onChange(documents) {

            setSubnets(documents);

        }

        function onError(error) {

            console.log(error);
            setPageStatus('error-fatal');

        }

        const unsubscribe = QueryListener('subnets', [], onLoad, onChange, onError, ['subnetid', 'asc']);

        return () => {
            unsubscribe();
        };

        // eslint-disable-next-line
    }, [getRoles, getUser]);

    //------------------------------------------------------
    //  HTML
    //------------------------------------------------------

    return (
        <PageComponent
            status={pageStatus}
            body={
                <div className='flex flex-col px-[15px] gap-[20px] w-[calc(100vw-40px)] max-h-[calc(100vh-220px)] overflow-hidden'>

                    {/* ----------------------------------- */}
                    {/*  Search & Action Buttons            */}
                    {/* ----------------------------------- */}

                    <div className='flex flex-row justify-between items-center gap-2'>

                        {/* ======== Search Bar ======== */}
                        <div className='grid grid-cols-[30px_1fr] items-center gap-[15px] bg-white px-[5px] rounded-[5px] border-1 border-solid border-[#dee2e6] w-[400px]'>
                            <img className='ml-[10px]' src={IconSearch} alt='searchInput-icon'></img>
                            <label htmlFor='searchInput'>
                                <input
                                    id='searchInput'
                                    className='border-none h-[37px] p-0 m-0 outline-none bg-white w-full'
                                    type='text'
                                    placeholder='Search'
                                    onChange={(e) => handleSearch(e.target.value)}
                                    value={searchInput}
                                    autoComplete='no'
                                ></input>
                            </label>
                        </div>

                        <div className='flex flex-row gap-2'>

                            {/* + Add Region/Supernet  */}
                            {
                                roleStatus === 'admin' ?
                                    <AddRegionSupernet></AddRegionSupernet>
                                    :
                                    <button disabled className='Primary-Button whitespace-nowrap flex flex-row gap-2'>
                                        <img src={IconAdd} alt='Add'></img> Add Region/Supernet
                                    </button>
                            }

                            {/* + Add Subnet */}
                            {
                                (roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin') ?
                                    <AddSubnet></AddSubnet>
                                    :
                                    <button disabled className='Primary-Button whitespace-nowrap flex flex-row gap-2'>
                                        <img src={IconAdd} alt='Add'></img> Add Subnet
                                    </button>
                            }

                            {/* Download */}
                            <ExportToCSV
                                filename={`Subnets-${Date.now()}`}
                                data={getDataObject(subnetsFiltered)}
                                disabled={!(roleStatus === 'download' || roleStatus === 'create' || roleStatus === 'write' || roleStatus === 'delete' || roleStatus === 'admin')}
                            ></ExportToCSV>

                            {/* Allocation Guide */}
                            <SubnetsAllocationGuide></SubnetsAllocationGuide>

                        </div>

                    </div>

                    {/* ----------------------------------- */}
                    {/*  Table Container                    */}
                    {/* ----------------------------------- */}

                    <FilterTags
                        tableArray={subnets}
                        setFilteredTableArray={setSubnetsFiltered}
                    ></FilterTags>

                    <div className='Table-Container flex flex-col w-full h-full overflow-hidden'>

                        {/* ======== Header ========== */}
                        <div className='flex flex-row justify-between py-[15px] px-[20px] rounded-tl-[10px] rounded-tr-[10px] border-b-[#D8D8D8] border-solid border-b bg-[#F5F5F5]'>

                            <label className='font-semibold self-center'>
                                Total: {subnetsFiltered.length} of {subnets.length}
                            </label>

                        </div>

                        {/* ======== Table Scrollable ========== */}
                        <div className='w-full overflow-auto'>

                            <FilterByPane
                                tableArray={subnets}
                            ></FilterByPane>

                            <table className='w-full'>
                                <thead>
                                    <tr className='sticky top-0 bg-white border-b-[#D8D8D8] border-solid border-b-2 z-10 box-border overflow-visible'>
                                        <TableHeader
                                            column='Subnet'
                                            objectKey='subnetip'
                                            sticky={true}
                                        ></TableHeader>
                                        <TableHeader
                                            column='Region'
                                            objectKey='region'
                                        ></TableHeader>
                                        <TableHeader
                                            column='Supernet'
                                            objectKey='supernet'
                                        ></TableHeader>
                                        <TableHeader
                                            column='Site ID'
                                            objectKey='siteid'
                                        ></TableHeader>
                                        <TableHeader
                                            column='Vlan ID'
                                            objectKey='vlan'
                                        ></TableHeader>
                                        <TableHeader
                                            column='Description'
                                            objectKey='description'
                                        ></TableHeader>
                                        <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap`}>
                                            Netmask
                                        </th>
                                        <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap`}>
                                            Hosts/Net
                                        </th>
                                        <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap`}>
                                            HostMin
                                        </th>
                                        <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap`}>
                                            HostMax
                                        </th>
                                        <TableHeader
                                            column='Additional Info (VRF, VPC, Zone …)'
                                            objectKey='additionalinfo'
                                        ></TableHeader>
                                        <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap`}>
                                            Created
                                        </th>
                                        <th className={`py-[15px] px-[20px] font-semibold whitespace-nowrap`}>
                                            Modified
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {
                                        // No subnets found
                                        subnetsFiltered.length === 0 ?
                                            (
                                                <tr>
                                                    <td colSpan={13}>
                                                        <div className='text-center p-[2%]'>
                                                            No subnets found.
                                                        </div>
                                                    </td>
                                                </tr>
                                            )
                                            :
                                            (
                                                // Map in each commercial item
                                                subnetsFiltered.map((subnet, index) => (

                                                    <tr key={index} className={`border-b-[#C8C8C9] border-solid border-b last:border-0 hover:bg-[#F0F7F7] ${subnetsFiltered.length === 1 && 'h-[250px]'}`}>
                                                        <td className='align-top py-[20px] px-[20px] border-solid left-0 drop-shadow-md bg-white sticky font-medium cursor-pointer hover:underline max-w-[30ch] whitespace-nowrap overflow-hidden text-ellipsis' onClick={() => navigate(`/inventory/subnet/${subnet?.subnetid}?view=overview`)}>{subnet?.subnetip}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>{subnet?.region}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>{subnet?.supernet}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r text-nowrap'>{subnet?.siteid}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>{subnet?.vlan}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r text-nowrap max-w-[60ch] overflow-hidden whitespace-wrap text-ellipsis'>
                                                            {subnet?.description}
                                                        </td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>{subnet?.subnetmask}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>{subnet?.assignablehosts}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>{subnet?.hostmin}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>{subnet?.hostmax}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r'>
                                                            {subnet?.additionalinfo}
                                                        </td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r text-nowrap w-[80px]'>{ConvertDate(subnet?.createddate)}</td>
                                                        <td className='align-top py-[20px] px-[20px] border-solid border-r text-nowrap w-[80px]'>{ConvertDate(subnet?.lastmodifieddate)}</td>
                                                    </tr>
                                                ))
                                            )
                                    }
                                </tbody>
                            </table>

                        </div>

                    </div>

                </div>
            }
        ></PageComponent>
    )
}
